<template>
  <div>
    <div
      v-if="subOptions && subOptions?.length > 0"
      class="inline-block w-full"
    >
      <div
        class="flex items-center justify-between h-12 w-full rounded-md px-2 text-on-surface leading-none font-semibold"
      >
        <NuxtLink
          v-if="to"
          :to="to"
          class="flex items-center flex-1 cursor-pointer"
          :class="{
            'text-primary': isOptionPathActive(to) || isSubOptionRoute,
          }"
        >
          <Icon :size="IconSize.LG" :name="icon" class="mx-2" />{{ label }}
        </NuxtLink>
        <button
          v-else
          class="flex items-center flex-1 text-left cursor-pointer"
          :class="{ 'text-primary': isSubOptionRoute }"
          @click="toggleSubOptions"
        >
          <Icon :size="IconSize.LG" :name="icon" class="mx-2" />{{ label }}
        </button>
        <button
          class="flex items-center cursor-pointer p-2"
          :aria-label="label"
          :aria-expanded="isOpened"
          :aria-controls="subOptionsId"
          @click="toggleSubOptions"
        >
          <Icon
            :size="IconSize.LG"
            :name="IconName.CHEVRON_DOWN"
            class="transition-all"
            :class="{ 'rotate-180': isOpened }"
          />
        </button>
      </div>
      <div
        v-show="isOpened"
        :id="subOptionsId"
        class="flex flex-col items-start gap-6 text-sm pl-4 border-l ml-7 py-3 text-on-surface font-semibold"
      >
        <NuxtLink
          v-for="(subOption, subOptionIndex) in subOptions"
          :key="subOptionIndex"
          :to="subOption.to"
          class="hover:text-primary transition-colors"
          :class="{ 'text-primary': isOptionPathActive(subOption.to) }"
        >
          {{ subOption.label }}
        </NuxtLink>
      </div>
    </div>
    <NuxtLink
      v-else
      :to="to"
      class="flex flex-col rounded-md w-full px-2 leading-none font-semibold cursor-pointer"
      :class="isTopOptionActive ? 'text-primary' : 'text-on-surface'"
    >
      <div class="flex items-center justify-between h-12 w-full">
        <div class="flex items-center">
          <Icon :size="IconSize.LG" :name="icon" class="mx-2" />{{ label }}
        </div>
        <Icon
          v-if="subOptions"
          :size="IconSize.LG"
          :name="IconName.CHEVRON_DOWN"
          :class="{ 'transition-all rotate-180': isOpen }"
        />
      </div>
    </NuxtLink>
  </div>
</template>
<script setup lang="ts">
import { Icon, IconName, IconSize } from '@exfluential/ui';
import { NuxtLink } from '#components';
import { useRoute } from 'vue-router';

type SidebarOption = {
  label: string;
  icon: IconName;
  to?: string;
  subOptions?: SidebarOption[];
};

const props = defineProps<SidebarOption>();
const route = useRoute();
const isOpen = ref<boolean>(false);

const normalizePath = (p: string) =>
  p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;

const currentPath = computed(() => normalizePath(route.path));

const isOptionPathActive = (path?: string) => {
  if (!path) return false;

  const target = normalizePath(path);
  const current = currentPath.value;

  if (target === '/') {
    return current === target;
  }

  return current === target || current.startsWith(`${target}/`);
};

const isTopOptionActive = computed(() => isOptionPathActive(props.to));

const isSubOptionRoute = computed(() =>
  Boolean(
    props.subOptions?.some((subOption) =>
      subOption.to ? isOptionPathActive(subOption.to) : false,
    ),
  ),
);

const subOptionsId = useId();
const isOpened = ref<boolean>(isSubOptionRoute.value);

const toggleSubOptions = () => {
  isOpened.value = !isOpened.value;
};
</script>
